import * as React from 'react'

import { F0Checkbox, F0Dialog, F0Text } from '@factorialco/f0-react'
import { Stack } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

import {
  resolveRequiredDependencies,
  type SchemaField,
} from 'modules/universal_importer/lib/resolveRequiredDependencies'

export interface FieldSelectionField {
  key: string
  label: string
  identifier?: boolean
  hidden?: boolean
  leDependant?: boolean
  category?: string | null
  validations?: SchemaField['validations']
}

// --- Super-group classification helpers ---

interface SuperGroup {
  titleKey: string
  fields: FieldSelectionField[]
  disabled: boolean
}

export function classifyField(field: FieldSelectionField): 'standard' | 'custom' | 'country' {
  if (!field.leDependant) return 'standard'
  // Custom fields have category 'custom' (set by backend custom_field_data_mapper).
  // Country-specific fields are leDependant via capabilities with country key
  // and keep their original YAML category (or null for contract fields).
  if (field.category?.toLowerCase().includes('custom')) return 'custom'
  return 'country'
}

function buildSuperGroups(visibleFields: FieldSelectionField[], isMultiLe: boolean): SuperGroup[] {
  const standard: Map<string, FieldSelectionField[]> = new Map()
  const custom: FieldSelectionField[] = []
  const country: FieldSelectionField[] = []

  for (const field of visibleFields) {
    const type = classifyField(field)
    if (type === 'standard') {
      const cat = field.category || 'others'
      const list = standard.get(cat) ?? []
      list.push(field)
      standard.set(cat, list)
    } else if (type === 'custom') {
      custom.push(field)
    } else {
      country.push(field)
    }
  }

  const groups: SuperGroup[] = []

  // Standard fields grouped by category, with 'others' last
  const standardEntries = [...standard.entries()].sort((a, b) => {
    if (a[0] === 'others') return 1
    if (b[0] === 'others') return -1
    return 0
  })
  for (const [cat, fields] of standardEntries) {
    groups.push({
      titleKey: cat,
      fields,
      disabled: false,
    })
  }

  if (custom.length > 0) {
    groups.push({
      titleKey: 'custom',
      fields: custom,
      disabled: isMultiLe,
    })
  }

  if (country.length > 0) {
    groups.push({
      titleKey: 'country',
      fields: country,
      disabled: isMultiLe,
    })
  }

  return groups
}

export function formatGroupTitle(titleKey: string): string {
  const knownKeys: Record<string, string> = {
    others: i18n.t('universal_importer.field_selection.others_fields'),
    custom: i18n.t('universal_importer.field_selection.custom_fields'),
    country: i18n.t('universal_importer.field_selection.country_fields'),
  }
  return (
    knownKeys[titleKey] ?? titleKey.charAt(0).toUpperCase() + titleKey.slice(1).replace(/_/g, ' ')
  )
}

// --- FieldsToEdit sub-component ---

export interface FieldsToEditProps {
  fields: FieldSelectionField[]
  selectedKeys: Set<string>
  onToggle: (key: string) => void
  isMultiLe: boolean
}

export const FieldsToEdit: React.FC<FieldsToEditProps> = ({
  fields,
  selectedKeys,
  onToggle,
  isMultiLe,
}) => {
  const visibleFields = React.useMemo(() => fields.filter((f) => !f.hidden), [fields])
  const superGroups = React.useMemo(
    () => buildSuperGroups(visibleFields, isMultiLe),
    [visibleFields, isMultiLe]
  )

  return (
    <Stack gap="lg">
      {superGroups.map((group) => (
        <Stack gap="sm" key={group.titleKey}>
          <F0Text
            variant="small"
            content={
              formatGroupTitle(group.titleKey) +
              (group.disabled
                ? ` (${i18n.t('universal_importer.field_selection.disabled_multi_le')})`
                : '')
            }
          />
          {group.fields.map((field) => {
            const isIdentifier = !!field.identifier
            const isChecked = group.disabled ? false : selectedKeys.has(field.key)

            return (
              <F0Checkbox
                key={field.key}
                title={field.label}
                checked={isChecked}
                onCheckedChange={() => onToggle(field.key)}
                disabled={isIdentifier || group.disabled}
              />
            )
          })}
        </Stack>
      ))}
    </Stack>
  )
}

// --- FieldSelectionModal (backward-compatible wrapper) ---

interface FieldSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (selectedFields: string[]) => void
  fields: FieldSelectionField[]
  isMultiLe?: boolean
  title?: string
  description?: string
}

export const FieldSelectionModal: React.FC<FieldSelectionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  fields,
  isMultiLe = false,
  title,
  description,
}) => {
  const visibleFields = React.useMemo(() => fields.filter((f) => !f.hidden), [fields])

  const [selectedKeys, setSelectedKeys] = React.useState<Set<string>>(() => {
    return new Set(visibleFields.map((f) => f.key))
  })

  React.useEffect(() => {
    setSelectedKeys(new Set(visibleFields.map((f) => f.key)))
  }, [visibleFields])

  // When isMultiLe changes, remove LE-dependent fields from selection
  React.useEffect(() => {
    if (isMultiLe) {
      setSelectedKeys((prev) => {
        const next = new Set(prev)
        for (const f of visibleFields) {
          if (f.leDependant) next.delete(f.key)
        }
        return next
      })
    }
  }, [isMultiLe, visibleFields])

  const toggleField = React.useCallback(
    (key: string) => {
      const field = visibleFields.find((f) => f.key === key)
      if (field?.identifier) return
      if (isMultiLe && field?.leDependant) return

      setSelectedKeys((prev) => {
        const next = new Set(prev)
        if (next.has(key)) {
          next.delete(key)
        } else {
          next.add(key)
        }
        return next
      })
    },
    [visibleFields, isMultiLe]
  )

  const handleConfirm = React.useCallback(() => {
    const resolved = resolveRequiredDependencies(Array.from(selectedKeys), fields)
    onConfirm(resolved.expandedSelection)
  }, [selectedKeys, fields, onConfirm])

  return (
    <F0Dialog
      isOpen={isOpen}
      title={title || i18n.t('universal_importer.field_selection.title')}
      description={description || i18n.t('universal_importer.field_selection.description')}
      onClose={onClose}
      primaryAction={{
        label: i18n.t('universal_importer.field_selection.confirm'),
        onClick: handleConfirm,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
      width="sm"
    >
      <FieldsToEdit
        fields={fields}
        selectedKeys={selectedKeys}
        onToggle={toggleField}
        isMultiLe={isMultiLe}
      />
    </F0Dialog>
  )
}
